import { motion, AnimatePresence } from "framer-motion";
import { useMemo } from "react";
import Stamp from "@/components/Stamp";

/**
 * Realistic envelope with tappable wax seal.
 * When breaking=true, the seal dissolves into a burst of tiny wax hearts drifting up.
 */
export const Envelope = ({
  sealed = true,
  recipient = "",
  sender = "",
  onSealClick,
  size = "lg",
  accent = "#991B1B",
  breaking = false,
  opened = false,
  stamp = null,
  pulse = false,
  dateBadge = null,
}) => {
  const dims =
    size === "lg" ? { w: 460, h: 290 } :
    size === "md" ? { w: 340, h: 220 } :
    { w: 260, h: 170 };

  // Precompute deterministic hearts so re-renders don't jitter.
  const hearts = useMemo(() => {
    const arr = [];
    const count = 28;
    for (let i = 0; i < count; i++) {
      const angle = (Math.random() * Math.PI) - Math.PI; // -180..0 (upward hemisphere)
      const dist = 60 + Math.random() * 140;
      arr.push({
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist - 80,
        r: 4 + Math.random() * 8,
        d: 1.4 + Math.random() * 1.6,
        delay: Math.random() * 0.25,
        sparkle: Math.random() > 0.55,
        drift: (Math.random() - 0.5) * 20,
        rot: (Math.random() - 0.5) * 90,
      });
    }
    return arr;
  }, [breaking]);

  return (
    <div
      className="relative select-none"
      style={{ width: dims.w, height: dims.h, perspective: "1200px" }}
      data-testid="envelope-visual"
    >
      {/* Envelope body */}
      <div
        className="absolute inset-0 rounded-[6px] overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #F5E7C9 0%, #E7D3A6 60%, #D6BB80 100%)",
          boxShadow:
            "0 30px 60px -20px rgba(120, 80, 40, 0.45), inset 0 -20px 30px rgba(80,50,20,0.15)",
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center px-8">
          <div className="text-center">
            <div className="font-hand text-[#3f2a1a] text-3xl md:text-4xl leading-tight">
              To {recipient || "You"}
            </div>
            <div className="mt-3 h-[1px] w-32 mx-auto bg-[#3f2a1a]/25" />
            <div className="mt-2 text-[10px] tracking-[0.35em] uppercase text-[#3f2a1a]/60">
              WrittenHearts
            </div>
          </div>
        </div>

        <svg
          className="absolute bottom-0 left-0"
          width={dims.w}
          height={dims.h / 2}
          viewBox={`0 0 ${dims.w} ${dims.h / 2}`}
        >
          <line x1="0" y1={dims.h / 2} x2={dims.w / 2} y2="10" stroke="rgba(60,40,15,0.25)" strokeWidth="1" />
          <line x1={dims.w} y1={dims.h / 2} x2={dims.w / 2} y2="10" stroke="rgba(60,40,15,0.25)" strokeWidth="1" />
        </svg>

        {stamp && (
          <div className="absolute top-3 right-3">
            <Stamp id={stamp} size={54} tilt={-8} />
          </div>
        )}

        {dateBadge && (
          <div className="absolute top-3 left-3">
            <div
              className="px-3 py-1.5 rounded-[2px] border border-[#7F1D1D]/40 bg-[#F3E7CE]/70 backdrop-blur-sm text-[#7F1D1D] font-serif-elegant text-xs md:text-sm tracking-widest uppercase"
              style={{ boxShadow: "inset 0 0 0 1px rgba(127,29,29,0.2)" }}
              data-testid="envelope-date-badge"
            >
              {dateBadge}
            </div>
          </div>
        )}
      </div>

      {/* Flap */}
      <motion.div
        className="absolute left-0 top-0 origin-top"
        style={{ width: dims.w, height: dims.h / 2 + 8, transformStyle: "preserve-3d" }}
        initial={false}
        animate={{ rotateX: opened ? -175 : 0 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <svg width={dims.w} height={dims.h / 2 + 8} viewBox={`0 0 ${dims.w} ${dims.h / 2 + 8}`} style={{ display: "block" }}>
          <defs>
            <linearGradient id="flapGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#EEDFC0" />
              <stop offset="1" stopColor="#CFB47F" />
            </linearGradient>
          </defs>
          <polygon
            points={`0,0 ${dims.w},0 ${dims.w / 2},${dims.h / 2 + 4}`}
            fill="url(#flapGrad)"
            stroke="rgba(60,40,15,0.35)"
            strokeWidth="1"
          />
        </svg>

        {/* Wax seal */}
        {sealed && (
          <motion.button
            type="button"
            aria-label={onSealClick ? "Tap to open the letter" : "Wax seal"}
            data-testid="envelope-wax-seal"
            onClick={(e) => { e.stopPropagation(); onSealClick && onSealClick(); }}
            className={`absolute wax-seal rounded-full flex items-center justify-center border-0 ${onSealClick ? "wax-seal-tappable" : ""} ${pulse ? "gentle-pulse" : ""}`}
            style={{
              width: 84, height: 84,
              left: dims.w / 2 - 42, top: dims.h / 2 - 58,
              background: `radial-gradient(circle at 35% 30%, #d64545 0%, ${accent} 45%, #4a0f0f 100%)`,
            }}
            initial={false}
            animate={
              breaking
                ? { scale: [1, 1.18, 0.7, 0], opacity: [1, 1, 0.6, 0], rotate: [0, -6, 12, 24] }
                : { scale: 1, opacity: 1, rotate: 0 }
            }
            transition={{ duration: 1.0, times: [0, 0.25, 0.7, 1], ease: "easeInOut" }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z"
                fill="#F5D6D6"
                stroke="#FFF4F4"
                strokeWidth="0.6"
              />
            </svg>
          </motion.button>
        )}
      </motion.div>

      {/* Wax hearts burst */}
      <AnimatePresence>
        {breaking && (
          <div
            className="absolute pointer-events-none z-30"
            style={{ left: dims.w / 2, top: dims.h / 2 - 20, width: 0, height: 0 }}
          >
            {hearts.map((h) => (
              <motion.div
                key={h.id}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.2, rotate: 0 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x: [0, h.x + h.drift * 0.3, h.x + h.drift],
                  y: [0, h.y * 0.5, h.y - 40],
                  scale: [0.3, 1, 0.8],
                  rotate: [0, h.rot],
                }}
                transition={{ duration: h.d, delay: h.delay, ease: "easeOut" }}
                exit={{ opacity: 0 }}
                style={{ position: "absolute", left: -h.r, top: -h.r }}
              >
                {h.sparkle ? (
                  <svg width={h.r * 2.4} height={h.r * 2.4} viewBox="0 0 24 24">
                    <path
                      d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"
                      fill="#F5D6D6"
                    />
                  </svg>
                ) : (
                  <svg width={h.r * 2} height={h.r * 2} viewBox="0 0 24 24">
                    <path
                      d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z"
                      fill={accent}
                      opacity="0.92"
                    />
                  </svg>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Envelope;
